import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import UIkit from "uikit";
import CRMService from "../../services/crm.service";
import Pagination from "../common/Pagination";
import { ActivityFormOffcanvas } from "./activity-panel";

const todayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const navigation = [["pipelines", "Pipelines"], ["owners", "Owners"], ["guests", "Guests"],
  ["properties", "Properties"], ["leads", "Leads"], ["housekeepers", "Housekeepers"],
  ["maintenance", "Maintenance"], ["vendors", "Vendors"], ["other", "Other"]];

export default function OpenTasks() {
  const [filters, setFilters] = useState({ search: "", assignee: "all", due: "all", page: 0 });
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [revision, setRevision] = useState(0);
  const saving = useRef(false);
  const change = (key, value) => setFilters((old) => ({ ...old, [key]: value, page: 0 }));
  useEffect(() => {
    const timer = setTimeout(() => setFilters((old) => old.search === search ? old : { ...old, search, page: 0 }), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    CRMService.getTaskAssignees().then(({ data }) => { if (active) setAssignees(data); })
      .catch(() => { if (active) setError("Unable to load assignee choices. Refresh to retry."); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    CRMService.listOpenTasks({ ...filters, per: 25, today: todayString() }).then(({ data }) => {
      if (!active) return;
      if (filters.page > 0 && !data.items.length && data.total <= filters.page * 25) {
        setFilters((old) => ({ ...old, page: Math.max(0, Math.ceil(data.total / 25) - 1) }));
      } else { setData(data); }
    }).catch(() => { if (active) setError("Unable to load tasks. Please retry."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, revision]);
  useEffect(() => {
    if (editing) UIkit.offcanvas("#offcanvas-crm-activity-form").show();
  }, [editing]);
  const save = async (type, payload) => {
    if (saving.current) return;
    saving.current = true;
    try {
      await CRMService.updateTask(payload.id, payload);
      UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
      setEditing(null); setRevision((n) => n + 1);
      UIkit.notification("Task updated", "success");
    } catch (_) { UIkit.notification("Unable to save task. Please try again.", "danger"); }
    finally { saving.current = false; }
  };
  const group = (label, items) => items.length > 0 && <section className="crm-section">
    <h3>{label}</h3>
    <div className="uk-overflow-auto"><table className="uk-table uk-table-divider uk-table-small">
      <thead><tr><th>Task</th><th>Related to</th><th>Assigned to</th><th>Due date</th><th>Status</th></tr></thead>
      <tbody>{items.map((task) => <tr key={task.id}>
        <td><button className="uk-button uk-button-link" type="button" onClick={() => setEditing({ ...task, status: ["in-progress", "in_progress"].includes(task.status) ? "started" : task.status })}>{task.title || "Untitled task"}</button></td>
        <td>{[task.guest_name, task.property_name, ...(task.work_order_assignees || [])
          .filter((a) => a.assignment_role === "owner").map((a) => a.assignee_name)].filter(Boolean).join(" · ") || "—"}</td>
        <td>{(task.work_order_assignees || []).filter((a) => a.assignment_role !== "owner")
          .map((a) => a.assignee_name).join(", ") || "Unassigned"}</td>
        <td>{task.due_at ? task.due_at.split("-").slice(1).concat(task.due_at.slice(0, 4)).join("/") : "No date"}</td>
        <td>{["new", "open"].includes(task.status) ? "Open" : "In progress"}</td>
      </tr>)}</tbody>
    </table></div>
  </section>;
  return <div className="crm-layout">
    <nav className="crm-sidebar" aria-label="CRM">
      <div className="crm-sidebar-header"><h3>CRM</h3></div>
      <div className="crm-sidebar-nav">
        <Link className="crm-sidebar-item crm-sidebar-child is-active" to="/crm/open-tasks">Open Tasks</Link>
        {navigation.map(([slug, label]) => <Link key={slug} className="crm-sidebar-item crm-sidebar-child" to={`/crm/${slug}`}>{label}</Link>)}
      </div>
    </nav>
    <main style={{ flex: 1, minWidth: 0, padding: 24, overflow: "auto" }}>
      <h2>Open Tasks</h2>
      <p>All outstanding CRM tasks, including tasks without a due date.</p>
      <div className="uk-flex uk-flex-wrap uk-flex-middle" style={{ gap: 12 }}>
        <input className="uk-input uk-form-width-medium" type="search" aria-label="Search tasks"
          placeholder="Search tasks or related people" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="uk-select uk-form-width-medium" aria-label="Assigned to" value={filters.assignee} onChange={(e) => change("assignee", e.target.value)}>
          <option value="all">All tasks</option><option value="mine">My tasks</option><option value="unassigned">Unassigned</option>
          {assignees.map((a) => <option value={a.id} key={a.id}>{a.name}</option>)}
        </select>
        <select className="uk-select uk-form-width-medium" aria-label="Due date" value={filters.due} onChange={(e) => change("due", e.target.value)}>
          <option value="all">All dates</option><option value="overdue">Overdue</option><option value="today">Today</option>
          <option value="upcoming">Upcoming</option><option value="none">No date</option>
        </select>
        <button type="button" className="uk-button uk-button-default" onClick={() => setRevision((n) => n + 1)}>Refresh</button>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p role="status">Loading tasks…</p> : !error && <>
        <p>{data.total.toLocaleString()} open tasks</p>
        {!data.items.length && <p>No open tasks match these filters.</p>}
        {group("No date", data.items.filter((t) => !t.due_at))}
        {group("Scheduled", data.items.filter((t) => t.due_at))}
        <Pagination index={filters.page} count={data.total} perPage={25}
          onIndexChanged={(page) => setFilters((old) => old.page === page ? old : { ...old, page })} />
      </>}
      <ActivityFormOffcanvas formType={editing ? "task" : null} editingItem={editing}
        taskOwnerValue={(editing?.work_order_assignees || []).filter((a) => a.assignment_role === "owner")
          .map((a) => `${a.assignee_type}_${a.assignee_id}`)[0] || null}
        onSave={save} onClose={() => setEditing(null)} />
    </main>
  </div>;
}
